'use client';

import type { MembershipBenefitBannerWidgetData } from '@weberus/shared-types';
import type { HomeLogPageNameValue } from '@/app/(pages)/sub-home/[category]/constants';
import { useState, type FC } from 'react';
import { cn } from '@yanolja-rnd/nol-design-system';
import { waitUntilClickTimeout } from '@weberus/utils';
import { clientLogStandardized, YaTrackerImpression } from '@weberus/client-tracker';
import { navigateTo } from '@/app/_shared/utils/action-navigator';
import { BaseImage } from '@/app/_components/common/base-image';

export interface MembershipBenefitBannerProps {
  data: MembershipBenefitBannerWidgetData;
  className?: string;
  pageNameLog: HomeLogPageNameValue;
}

/**
 *  멤버쉽 베네핏 배너
 *  @see {@link https://www.figma.com/design/zlHuiOHmYv1ghVDbTHUSvW/%5BGUIDE%5D-%EC%9C%84%EC%A0%AF%2F%EC%98%A4%EB%8D%94%EB%A7%81?node-id=4-13&node-type=canvas&t=f63UbsEXFcnLs9Or-0}
 */
export const MembershipBenefitBanner: FC<MembershipBenefitBannerProps> = ({ data, className, pageNameLog }) => {
  const { items, logMeta: containerLogMeta } = data;
  const { image, action, logMeta: itemLogMeta } = items?.[0] ?? {};

  const [isError, setIsError] = useState(false);

  if (!image) {
    return null;
  }

  const handleClick = async (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    await waitUntilClickTimeout(
      clientLogStandardized('click', pageNameLog, {
        ...containerLogMeta,
        ...itemLogMeta,
        objectType: 'OBJECT_TYPE_BANNER',
      }),
    );

    navigateTo(action);
  };

  return (
    <section
      className={cn('flex w-full justify-center p-16 pc:w-pc-size pc:px-0 pc:py-40', { hidden: isError }, className)}>
      <div className="flex w-full cursor-pointer" onClick={handleClick}>
        <div className="relative h-80 w-full overflow-hidden rounded-8 pc:rounded-16">
          <BaseImage
            src={image.url}
            alt="혜택 배너"
            className="object-cover"
            onError={() => {
              setIsError(true);
            }}
          />
          <YaTrackerImpression
            onImpressed={() =>
              clientLogStandardized('impression', pageNameLog, {
                ...containerLogMeta,
                ...itemLogMeta,
                objectType: 'OBJECT_TYPE_BANNER',
              })
            }>
            <div aria-hidden />
          </YaTrackerImpression>
        </div>
      </div>
    </section>
  );
};
